import React, { useEffect, useState } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import ExpressionUploader from './ExpressionUploader'
import { EXPRESSIONS, RARITIES } from './constants'

/**
 * Shared create/edit form for a mascot character. Holds scalar fields plus the
 * six expression images and notifies the parent (via `onChange`) of the current
 * `{ payload, isValid }` whenever anything changes — the parent submits the
 * latest payload. `payload` carries the scalars plus only the images the teacher
 * picked, each as the `{base64, name}` shape `admin.mascots.*` expects.
 */
export default function MascotForm({ initial = {}, errorMessage, onChange }) {
  const [slug, setSlug] = useState(initial.slug || '')
  const [name, setName] = useState(initial.name || '')
  const [rarity, setRarity] = useState(initial.rarity || 'common')
  const [cost, setCost] = useState(initial.cost ?? 0)
  const [tag, setTag] = useState(initial.tag || '')
  const [description, setDescription] = useState(initial.description || '')
  const [isActive, setIsActive] = useState(initial.is_active ?? true)
  const [isFeatured, setIsFeatured] = useState(initial.is_featured ?? false)
  const [images, setImages] = useState({})
  // One textarea per expression; one line per speech variant (#272).
  const [speechText, setSpeechText] = useState(() => {
    const initialSpeech = initial.speech || {}
    return Object.fromEntries(
      EXPRESSIONS.map((expression) => [expression, (initialSpeech[expression] || []).join('\n')])
    )
  })

  // idle art is mandatory — the backend requires it on create and hides art-less
  // characters from the store. On edit it's already present (initial.expressions).
  const hasIdle = Boolean(images.idle) || Boolean((initial.expressions || {}).idle)

  useEffect(() => {
    const isValid = slug.trim().length > 0 && name.trim().length > 0 && hasIdle
    const payload = {
      slug: slug.trim(),
      name: name.trim(),
      tag: tag.trim(),
      description: description.trim(),
      rarity,
      cost: Number(cost) || 0,
      is_active: isActive,
      is_featured: isFeatured,
      speech: Object.fromEntries(
        EXPRESSIONS.map((expression) => [
          expression,
          (speechText[expression] || '')
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean),
        ]).filter(([, lines]) => lines.length)
      ),
      ...Object.fromEntries(
        Object.entries(images).map(([expression, file]) => [`image_${expression}`, file])
      ),
    }
    onChange({ payload, isValid })
  }, [
    slug,
    name,
    tag,
    description,
    rarity,
    cost,
    isActive,
    isFeatured,
    images,
    speechText,
    hasIdle,
    onChange,
  ])

  const onImageChange = (expression, file) => {
    setImages((prev) => ({ ...prev, [expression]: file }))
  }

  const onSpeechChange = (expression, value) => {
    setSpeechText((prev) => ({ ...prev, [expression]: value }))
  }

  const existingExpressions = initial.expressions || {}

  return (
    <Box sx={{ flexGrow: 1, marginTop: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper>
            <Stack padding={2} spacing={2}>
              <TextField
                label="Slug"
                error={slug.trim().length < 1}
                helperText={slug.trim().length > 0 ? 'e.g. pig01, dragon' : 'Slug is required'}
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
              />
              <TextField
                label="Name"
                error={name.trim().length < 1}
                helperText={name.trim().length > 0 ? '' : 'Name is required'}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                label="Tag"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                helperText="Short subtitle, e.g. “Нічний мисливець”."
              />
              <TextField
                label="Description"
                multiline
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                helperText="Longer tagline shown on the store hero / purchase modal."
              />
              <TextField
                select
                label="Rarity"
                value={rarity}
                onChange={(e) => setRarity(e.target.value)}
              >
                {RARITIES.map((option) => (
                  <MenuItem key={option} value={option} sx={{ textTransform: 'capitalize' }}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Cost (stars)"
                type="number"
                inputProps={{ min: 0 }}
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                helperText="What the store charges. 0 for free."
              />
              <FormControlLabel
                control={
                  <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                }
                label="Active (shown in the catalog)"
              />
              <FormControlLabel
                control={
                  <Switch checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} />
                }
                label="Featured (store hero — “Маскот тижня”)"
              />
            </Stack>
          </Paper>
          {errorMessage && (
            <Paper sx={{ marginTop: 2 }}>
              <Alert severity="error">{errorMessage}</Alert>
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Expressions ({EXPRESSIONS.length}) — <code>idle</code> is required; missing ones fall back to it
          </Typography>
          {!hasIdle && (
            <Typography variant="body2" color="error" gutterBottom>
              Upload the <code>idle</code> image — a mascot can&apos;t enter the store without art.
            </Typography>
          )}
          <Grid container spacing={1}>
            {EXPRESSIONS.map((expression) => (
              <Grid item xs={6} sm={4} key={expression}>
                <ExpressionUploader
                  expression={expression}
                  required={expression === 'idle'}
                  value={images[expression]}
                  existingUrl={existingExpressions[expression]}
                  onChange={onImageChange}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Speech — one line per variant; the mascot says a random one
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Per expression. Leave blank to say nothing for that mood (it falls back to{' '}
            <code>idle</code>).
          </Typography>
          <Grid container spacing={1}>
            {EXPRESSIONS.map((expression) => (
              <Grid item xs={12} sm={6} md={4} key={expression}>
                <TextField
                  label={expression}
                  fullWidth
                  multiline
                  minRows={3}
                  value={speechText[expression]}
                  onChange={(e) => onSpeechChange(expression, e.target.value)}
                  placeholder={'Одна фраза на рядок'}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}
